import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { Message } from "@redotech/redo-model/agents/llm-messages";
import { useState } from "react";
import { useEscalationContext } from "./escalation-context";

interface UseInChatEscalationParams {
  messages: Message[];
  input: unknown;
  onSuccess: () => void;
}

export function useInChatEscalation({
  messages,
  input,
  onSuccess,
}: UseInChatEscalationParams) {
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const escalationContext = useEscalationContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEscalated, setIsEscalated] = useState(false);

  const escalate = useHandler(async () => {
    if (!escalationContext.triageThreadId) {
      setError("Unable to escalate: conversation thread not found");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const chatHistory = messages
        .filter((msg) => msg.role !== "tool")
        .map((msg) => {
          let content: string;
          if (msg.role === "system") {
            content = msg.content;
          } else if (typeof msg.content === "string") {
            content = msg.content;
          } else if (Array.isArray(msg.content)) {
            content = msg.content
              .filter(
                (part): part is { type: "text"; text: string } =>
                  part.type === "text",
              )
              .map((part) => part.text)
              .join("\n");
          } else {
            content = "";
          }
          return { id: msg.id ?? undefined, role: msg.role, content };
        }) as Array<{
        id?: string;
        role: "system" | "user" | "assistant";
        content: string;
      }>;

      const toolInput = input as
        | { reason?: string; category?: string }
        | undefined;

      const result = await merchantRpcClient.createEscalation({
        triageThreadId: escalationContext.triageThreadId,
        chatHistory,
        escalationReason: toolInput?.reason,
        issueCategory: toolInput?.category,
      });

      escalationContext.setEscalation({
        escalationId: result.escalationId,
        intercomConversationId: result.intercomConversationId,
        noticeTopic: result.noticeTopic,
        status: "waiting",
      });

      setIsLoading(false);
      setIsEscalated(true);
      onSuccess();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to connect to support";
      setError(errorMessage);
      setIsLoading(false);
    }
  });

  return { escalate, isLoading, error, isEscalated };
}
